import { atomWithStorage } from 'jotai/utils';
import { atomWithMutation, atomWithQuery } from 'jotai-tanstack-query';
import { isAxiosError } from 'axios';

import { instance, noAuthInstance } from '@/interfaces';
import { userInfoAtom } from '@/interfaces/member';

import type { Getter } from 'jotai';
import type { AtomWithQueryResult } from 'jotai-tanstack-query';
import type { APIResponse } from '@/@types/types';
import type { TokenInfo, LoginFormData, SignUpData, PermissionData, UserInfo } from '@/@types/user';

export const AUTH_QUERY_NAME = 'AUTH_';

const contextPath = '/auth';

const tokenInit: TokenInfo = { accessToken: '', refreshToken: '' };
export const tokenAtom = atomWithStorage('TOKEN', tokenInit);

export const loginAtom = atomWithMutation(() => ({
  mutationKey: [AUTH_QUERY_NAME + 'TOKEN'],
  mutationFn: async ({ email, password }: LoginFormData) => {
    return login(email, password);
  },
}));

const login = async (email: FormDataEntryValue, password: FormDataEntryValue) => {
  const { data } = await noAuthInstance.post<APIResponse<TokenInfo>>(contextPath + '/login', { email, password });
  return data.data;
};

export const logoutAtom = atomWithMutation(() => ({
  mutationFn: async () => {
    const isSuccess = await postLogout();
    return isSuccess;
  },
}));
async function postLogout() {
  try {
    const { data } = await instance.post<APIResponse>(`/auth/logout`);
    if (data.errorCode !== 'OK') {
      console.error('[postLogout] API error code:', data.errorCode);
      return false;
    }
    return true;
  } catch (err) {
    if (isAxiosError(err)) {
      if (err.response) {
        console.error('[postLogout] API error code:', err.response.data.errorCode);
      } else {
        console.error('[postLogout] Axios error message:', err.message);
      }
    } else {
      console.error('[postLogout] Unknown error', err);
    }
    return false;
  }
}

export const signUpAtom = atomWithMutation(() => ({
  mutationKey: [AUTH_QUERY_NAME + 'SIGN_UP'],
  mutationFn: async (body: SignUpData) => {
    return postSignUp(body);
  },
}));

const postSignUp = async (body: SignUpData) => {
  return await noAuthInstance.post(contextPath + '/sign-up', body);
};

export const passwordChangeAtom = atomWithMutation(() => ({
  mutationKey: [AUTH_QUERY_NAME + 'PWD_CHG'],
  mutationFn: async (body: string) => {
    return postPwdChg(body);
  },
}));

const postPwdChg = async (body: string) => {
  return await instance.put(contextPath + '/password', { password: body });
};

export const permissionChangeAtom = atomWithMutation(() => ({
  mutationFn: async (data: PermissionData) => {
    const isSuccess = await postPermissionData(data);
    return isSuccess;
  },
}));
async function postPermissionData(body: PermissionData) {
  try {
    const { data } = await instance.post<APIResponse>(`/auth/permission/change`, body);
    if (data.errorCode !== 'OK') {
      console.error('[postPermissionData] API error code:', data.errorCode);
      return false;
    }
    return true;
  } catch (err) {
    if (isAxiosError(err)) {
      console.error('[postPermissionData] API error code:', err.response.data.errorCode);
    } else {
      console.error('[postPermissionData] Unknown error', err);
    }
    return false;
  }
}

export const stockLoginAtom = atomWithQuery((get: Getter) => ({
  queryKey: ['STOCK_LOGIN', get(userInfoAtom)],
  queryFn: async ({ queryKey: [, userInfoAtomValue] }) => {
    const { data: userInfo } = userInfoAtomValue as AtomWithQueryResult<UserInfo>;
    const token = await postStockLogin(userInfo.site[0].id);
    if (!token) return Promise.reject();

    return token;
  },
}));
async function postStockLogin(siteId: string) {
  try {
    const { data } = await instance.post<APIResponse<TokenInfo>>('/stock/auth/login', { siteId });
    return data.data;
  } catch (err) {
    if (isAxiosError(err)) {
      if (err.response) {
        console.error('[postStockLogin] API error:', err.response.data.errorCode);
      } else {
        console.error('[postStockLogin] Axios error message:', err.message);
      }
    } else {
      console.error('[postStockLogin] Unknown error', err);
    }
    return null;
  }
}
